/* ==========================================================================
   Acadiana Made — delivery-estimate.js
   Upgrades every [data-delivery-estimate] rendered by
   snippets/delivery-estimate.liquid: recomputes the "made and shipped by"
   window client-side (the server-rendered sentence is a snapshot that goes
   stale in the page cache) and ticks the order-cutoff countdown every 30s.

   Day math runs on "wall clock" Date objects: the UTC fields hold the shop's
   (or the visitor's) local wall-clock components, so adding days is
   DST-proof and weekday checks are unambiguous.

   Element data attributes (all optional except the selector itself):
     data-cutoff="14:00"        daily cutoff, H or H:MM (24h)
     data-tz-offset="-0500"     shop UTC offset at render time; "local" =
                                use the visitor's clock; missing/invalid = local
     data-min-days / data-max-days   production window in business days
     data-exclude-days="SAT,SUN"     weekdays that never count / never ship
     data-date-format="ddd, MMM D"   tokens: YYYY YY MMMM MMM MM M Do DD D dddd ddd
     data-text                  template with [time] [from] [to] (countdown on)
     data-text-static           template used when the countdown is off
     data-text-fixed            template used when data-ships-after is a
                                future date (no countdown in that mode)
     data-ships-after="YYYY-MM-DD"   fixed earliest ship date (drops)
     data-show-countdown="true|false"
     data-units="d,h,m"         suffixes for days / hours / minutes
   Public API: window.AMDeliveryEstimate.tick() re-renders everything now
   (call after injecting markup, e.g. quick view); .refresh(el) also
   re-reads that element's data attributes (.refresh() with no argument
   re-reads every element — use after changing data-* attributes).
   ========================================================================== */
(function () {
  'use strict';
  if (window.AMDeliveryEstimate) return;

  var SEL = '[data-delivery-estimate]';
  var TICK_MS = 30000;
  var MAX_WALK = 400; /* safety cap for day-walking loops */
  var DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  var DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MON_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var TOKEN_RE = /YYYY|YY|MMMM|MMM|MM|M|Do|DD|D|dddd|ddd/g;

  /* ------------------------------- helpers ------------------------------ */
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function ordinal(n) {
    var m100 = n % 100, m10 = n % 10;
    if (m100 >= 11 && m100 <= 13) return n + 'th';
    if (m10 === 1) return n + 'st';
    if (m10 === 2) return n + 'nd';
    if (m10 === 3) return n + 'rd';
    return n + 'th';
  }
  /* '-0500' | '+05:30' | 'Z' -> minutes east of UTC; null when unparseable */
  function parseOffset(str) {
    if (!str) return null;
    str = String(str).replace(/^\s+|\s+$/g, '');
    if (/^z$/i.test(str)) return 0;
    var m = /^([+-])(\d{1,2}):?(\d{2})$/.exec(str);
    if (!m) return null;
    var min = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    return m[1] === '-' ? -min : min;
  }
  /* 'YYYY-MM-DD' (or an ISO date-time; only the date part is used) */
  function parseDate(str) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str || '').replace(/^\s+|\s+$/g, ''));
    if (!m) return null;
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  /* ---------------------------- wall-clock math ------------------------- */
  function wallNow(cfg) {
    var d = new Date();
    if (cfg.tz === 'local') {
      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
    }
    return new Date(d.getTime() + cfg.offset * 60000);
  }
  function dayStart(w) { return new Date(Date.UTC(w.getUTCFullYear(), w.getUTCMonth(), w.getUTCDate())); }
  function addDays(w, n) { var d = new Date(w.getTime()); d.setUTCDate(d.getUTCDate() + n); return d; }
  function atCutoff(day, cfg) { return new Date(day.getTime() + (cfg.cutoffH * 60 + cfg.cutoffM) * 60000); }
  function isExcluded(w, ex) { return ex.indexOf(DOW[w.getUTCDay()]) !== -1; }
  /* Walk forward one calendar day at a time; only non-excluded days count.
     (Ella's version skipped at most one day per excluded weekday.) */
  function addBusinessDays(day, n, ex) {
    var d = day, added = 0, guard = 0;
    while (added < n && guard++ < MAX_WALK) {
      d = addDays(d, 1);
      if (!isExcluded(d, ex)) added++;
    }
    return d;
  }
  function nextWorkingDay(day, ex) {
    var d = day, guard = 0;
    while (isExcluded(d, ex) && guard++ < 14) d = addDays(d, 1);
    return d;
  }

  /* ------------------------------ formatting ---------------------------- */
  function formatDate(w, fmt) {
    return fmt.replace(TOKEN_RE, function (t) {
      switch (t) {
        case 'YYYY': return String(w.getUTCFullYear());
        case 'YY': return pad2(w.getUTCFullYear() % 100);
        case 'MMMM': return MON_LONG[w.getUTCMonth()];
        case 'MMM': return MON_LONG[w.getUTCMonth()].slice(0, 3);
        case 'MM': return pad2(w.getUTCMonth() + 1);
        case 'M': return String(w.getUTCMonth() + 1);
        case 'Do': return ordinal(w.getUTCDate());
        case 'DD': return pad2(w.getUTCDate());
        case 'D': return String(w.getUTCDate());
        case 'dddd': return DOW_LONG[w.getUTCDay()];
        case 'ddd': return DOW_LONG[w.getUTCDay()].slice(0, 3);
      }
      return t;
    });
  }
  /* "1d 4h" | "4h 12m" | "12m" — never "0m" (ceil to the next minute) */
  function formatRemaining(ms, units) {
    var total = Math.max(1, Math.ceil(ms / 60000));
    var d = Math.floor(total / 1440), h = Math.floor((total % 1440) / 60), m = total % 60;
    if (d > 0) return d + units[0] + ' ' + h + units[1];
    if (h > 0) return h + units[1] + ' ' + m + units[2];
    return m + units[2];
  }

  /* -------------------------------- config ------------------------------ */
  function readConfig(el) {
    var attr = function (n, fb) { var v = el.getAttribute(n); return v === null ? fb : v; };
    var raw = attr('data-exclude-days', '').toUpperCase().replace(/\s+/g, '').split(',');
    var ex = [];
    for (var i = 0; i < raw.length; i++) {
      if (DOW.indexOf(raw[i]) !== -1 && ex.indexOf(raw[i]) === -1) ex.push(raw[i]);
    }
    if (ex.length >= 7) ex = []; /* every day excluded: nothing can be skipped */

    var cut = /^(\d{1,2})(?::(\d{1,2}))?$/.exec(attr('data-cutoff', '').replace(/\s+/g, ''));
    var tzAttr = attr('data-tz-offset', '');
    var offset = parseOffset(tzAttr);
    var min = parseInt(attr('data-min-days', '3'), 10); if (isNaN(min) || min < 0) min = 0;
    var max = parseInt(attr('data-max-days', '5'), 10); if (isNaN(max) || max < min) max = min;
    var units = attr('data-units', 'd,h,m').split(',');
    while (units.length < 3) units.push('');

    return {
      tz: (tzAttr.toLowerCase() === 'local' || offset === null) ? 'local' : 'shop',
      offset: offset || 0,
      cutoffH: cut ? Math.min(23, parseInt(cut[1], 10)) : 14,
      cutoffM: (cut && cut[2]) ? Math.min(59, parseInt(cut[2], 10)) : 0,
      min: min,
      max: max,
      ex: ex,
      fmt: attr('data-date-format', 'ddd, MMM D'),
      text: attr('data-text', ''),
      textStatic: attr('data-text-static', ''),
      textFixed: attr('data-text-fixed', ''),
      shipsAfter: parseDate(attr('data-ships-after', '')),
      countdown: attr('data-show-countdown', 'true') !== 'false',
      units: units
    };
  }

  /* -------------------------------- compute ----------------------------- */
  function compute(cfg) {
    var now = wallNow(cfg);
    var today = dayStart(now);

    /* Fixed "ships after" date in the future overrides the rolling estimate.
       Once that date has passed we fall back to the rolling window. */
    if (cfg.shipsAfter && cfg.shipsAfter.getTime() >= today.getTime()) {
      return {
        mode: 'fixed',
        from: cfg.shipsAfter,
        to: addBusinessDays(cfg.shipsAfter, cfg.max - cfg.min, cfg.ex),
        remaining: 0
      };
    }

    /* Production starts today if today is a working day and we're before the
       cutoff; otherwise on the next working day. The countdown targets that
       day's cutoff, so weekend visitors see "Order within 1d 16h". */
    var start = today;
    if (isExcluded(today, cfg.ex) || now.getTime() >= atCutoff(today, cfg).getTime()) {
      start = nextWorkingDay(addDays(today, 1), cfg.ex);
    }
    var from = addBusinessDays(start, cfg.min, cfg.ex);
    return {
      mode: cfg.countdown ? 'countdown' : 'static',
      from: from,
      to: addBusinessDays(from, cfg.max - cfg.min, cfg.ex),
      remaining: atCutoff(start, cfg).getTime() - now.getTime()
    };
  }

  /* -------------------------------- render ------------------------------ */
  function setText(root, sel, value) {
    var nodes = root.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== value) nodes[i].textContent = value;
    }
  }
  function render(el, cfg, st) {
    var textEl = el.querySelector('[data-delivery-estimate-text]');
    if (!textEl) return;
    var tpl = st.mode === 'fixed' ? cfg.textFixed : (st.mode === 'countdown' ? cfg.text : cfg.textStatic);
    if (!tpl) tpl = cfg.text || cfg.textStatic || cfg.textFixed || '';

    /* Rebuild the sentence only when the template changes (mode flip);
       otherwise just update the token spans in place. */
    if (el.__deTpl !== tpl) {
      textEl.innerHTML = esc(tpl)
        .replace(/\[time\]/g, '<strong class="delivery-estimate__time" data-de-time></strong>')
        .replace(/\[from\]/g, '<strong class="delivery-estimate__date" data-de-from></strong>')
        .replace(/\[to\]/g, '<strong class="delivery-estimate__date" data-de-to></strong>');
      el.__deTpl = tpl;
    }
    setText(textEl, '[data-de-time]', st.mode === 'countdown' ? formatRemaining(st.remaining, cfg.units) : '');
    setText(textEl, '[data-de-from]', formatDate(st.from, cfg.fmt));
    setText(textEl, '[data-de-to]', formatDate(st.to, cfg.fmt));

    if (el.getAttribute('data-delivery-mode') !== st.mode) el.setAttribute('data-delivery-mode', st.mode);
    if (st.mode === 'fixed') el.classList.add('delivery-estimate--fixed');
    else el.classList.remove('delivery-estimate--fixed');
  }

  /* -------------------------------- runtime ----------------------------- */
  function tick() {
    var els = document.querySelectorAll(SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var cfg = el.__deCfg || (el.__deCfg = readConfig(el));
      try { render(el, cfg, compute(cfg)); } catch (e) { /* leave the server-rendered sentence */ }
    }
  }
  var timer = null;
  function start() {
    tick();
    if (!timer) timer = window.setInterval(tick, TICK_MS);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  /* background tabs throttle timers; catch up as soon as we're visible */
  document.addEventListener('visibilitychange', function () { if (!document.hidden) tick(); });
  /* theme editor re-renders */
  document.addEventListener('shopify:section:load', tick);

  window.AMDeliveryEstimate = {
    tick: tick,
    /* refresh(el): re-read that element's data attributes and re-render.
       refresh() with no argument re-reads every [data-delivery-estimate]. */
    refresh: function (el) {
      var els = el ? [el] : document.querySelectorAll(SEL);
      for (var i = 0; i < els.length; i++) els[i].__deCfg = null;
      tick();
    }
  };
})();
